import { analytics, AnalyticsConst, AnalyticsStandardEvent } from '@qbe/qal';
import {
  BusinessErrorPayload,
  BuyStart,
  EcommerceGenericPurchasePayload,
  QuoteStart,
  TechnicalErrorPayload,
  TransactionStart,
} from '@qbe/qal/src/types';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  analyticsConstant,
  AnalyticsStandardEvents,
  AnalyticsTransactionType,
} from 'types/analytics';
import mapStore from 'utils/analytics/mapStore';
import { qalTrackPageView } from 'utils/analytics/trackPageView';
import { updatePayMyBillConfig } from 'utils/analytics/trackUtils';

import { RootState } from '../store';

/**
 * QAL Thunk
 */
interface QALTrackEventOptions<T> {
  name: string;
  handler: (payload: T, store: RootState) => void;
}
interface AnalyticsOmptizeActivate extends Omit<AnalyticsStandardEvent, 'event'> {
  event?: string;
  eventAction: string;
  virtualPagePath: string;
}
const createQALTrackEvent = <T>({ name, handler }: QALTrackEventOptions<T>) =>
  createAsyncThunk<void, T, { state: RootState }>(
    `analytics/qal-${name}-event`,
    async (payload, { getState }) => {
      const state = getState();
      handler(payload, state);
    }
  );

// QAL Track Page View Event
export const qalTrackPageViewEvent = createQALTrackEvent<string>({
  name: 'pageView',
  handler: (path, state) => {
    // Assuming this function returns a boolean
    updatePayMyBillConfig();

    // Call the handler with the additional information
    qalTrackPageView(state, path);
  },
});

// QAL Track Transaction Start Event
export const qalTrackTransactionStartAction = createQALTrackEvent<string>({
  name: 'transactionStart',
  handler: (referenceNumber, state) => {
    // Call the handler with the additional information
    analytics.trackTransactionStart({
      eventLabel: referenceNumber,
      data: {
        transactionType: AnalyticsTransactionType.renewalPayment,
        ...mapStore(state),
      },
    });
  },
});

// QAL Track Transaction Complete Event
export const qalTrackTransactionCompleteAction = createQALTrackEvent<TransactionStart>({
  name: 'transactionComplete',
  handler: (payload, state) => {
    // Call the handler with the additional information
    analytics.trackTransactionComplete({
      eventLabel: payload.eventLabel,
      data: {
        ...payload.data,
        appComplete: true,
        ...mapStore(state),
      },
    });
  },
});

export const qalTrackEventAction = createQALTrackEvent<
  AnalyticsStandardEvent | AnalyticsOmptizeActivate
>({
  name: 'trackEvent',
  handler: (payload, state) => {
    // Call the handler with the additional information
    analytics.trackCustomEvent({
      event: payload.event || AnalyticsStandardEvents.Standard,
      eventAction: payload.eventAction || analyticsConstant.NOT_SET,
      eventLabel: payload.eventLabel || analyticsConstant.NOT_SET,
      data: {
        ...payload.data,
        ...mapStore(state),
      },
    });
  },
});

export const qalTrackOptimizeEventAction = createQALTrackEvent<AnalyticsOmptizeActivate>({
  name: 'trackEvent',
  handler: (payload, state) => {
    // Call the handler with the additional information
    analytics.trackCustomEvent({
      event: payload.event || AnalyticsStandardEvents.Standard,
      eventAction: payload.eventAction || analyticsConstant.NOT_SET,
      eventLabel: payload.eventLabel || analyticsConstant.NOT_SET,
      data: {
        ...payload.data,
        ...mapStore(state),
      },
    });
  },
});

export const qalTrackRenewalPaymentPurchaseAction =
  createQALTrackEvent<EcommerceGenericPurchasePayload>({
    name: 'renewal-payment-purchase',
    handler: (payload, state) => {
      // Merge mapStore(state) and payload.data into a single object
      const mergedData = { ...mapStore(state), ...payload.data };

      // Merge the original payload with the updated data
      const updatedPayload: EcommerceGenericPurchasePayload = {
        ...payload,
        data: mergedData,
      };

      // Call the handler with the additional information
      analytics.trackEcommercePurchase(updatedPayload);
    },
  });

export const qalTrackQuoteStartAction = createQALTrackEvent<QuoteStart | undefined>({
  name: 'quoteStart',
  handler: (_, state) => {
    // Call the handler with the additional information
    analytics.trackQuoteStart({
      data: mapStore(state),
    });
  },
});

export const qalTrackBuyStartAction = createQALTrackEvent<BuyStart | undefined>({
  name: 'buyStart',
  handler: (_, state) => {
    const quoteNumber = (mapStore(state).quoteNumber as string) || AnalyticsConst.NOT_SET;
    // Call the handler with the additional information
    analytics.trackBuyStart({
      data: {
        quoteNumber,
        ...mapStore(state),
      },
    });
  },
});

export const qalTrackBusinessErrorAction = createQALTrackEvent<
  BusinessErrorPayload | BusinessErrorPayload[] | undefined
>({
  name: 'businessError',
  handler: (payload, state) => {
    if (Array.isArray(payload)) {
      // If payload is an array, dispatch each action in the array
      payload.forEach((action) => {
        analytics.trackBusinessError({
          errorCode: action?.errorCode || AnalyticsConst.NOT_SET,
          message: action?.message || AnalyticsConst.NOT_SET,
          questionId: action?.questionId || AnalyticsConst.NOT_SET,
          errorType: action?.errorType || AnalyticsConst.NOT_SET,
          data: mapStore(state),
        });
      });
    } else {
      // If payload is a single action, dispatch it
      analytics.trackBusinessError({
        errorCode: payload?.errorCode || AnalyticsConst.NOT_SET,
        message: payload?.message || AnalyticsConst.NOT_SET,
        questionId: payload?.questionId || AnalyticsConst.NOT_SET,
        errorType: payload?.errorType || AnalyticsConst.NOT_SET,
        data: mapStore(state),
      });
    }
  },
});

export const qalTrackTechnicalErrorAction = createQALTrackEvent<
  TechnicalErrorPayload | TechnicalErrorPayload[] | undefined
>({
  name: 'technicalError',
  handler: (payload, state) => {
    if (Array.isArray(payload)) {
      // If payload is an array, dispatch each action in the array
      payload.forEach((action) => {
        analytics.trackTechnicalError({
          errorCode: action?.errorCode || AnalyticsConst.NOT_SET,
          message: action?.message || AnalyticsConst.NOT_SET,
          questionId: action?.questionId || AnalyticsConst.NOT_SET,
          errorType: action?.errorType || AnalyticsConst.NOT_SET,
          data: mapStore(state),
        });
      });
    } else {
      // If payload is a single action, dispatch it
      analytics.trackTechnicalError({
        errorCode: payload?.errorCode || AnalyticsConst.NOT_SET,
        message: payload?.message || AnalyticsConst.NOT_SET,
        questionId: payload?.questionId || AnalyticsConst.NOT_SET,
        errorType: payload?.errorType || AnalyticsConst.NOT_SET,
        data: mapStore(state),
      });
    }
  },
});

export const qalFormStartAction = createQALTrackEvent<undefined>({
  name: 'formStart',
  handler: (_, state) => {
    analytics.trackFormStart({
      data: mapStore(state),
    });
  },
});

export const qalFormCompleteAction = createQALTrackEvent<undefined>({
  name: 'formComplete',
  handler: (_, state) => {
    analytics.trackFormComplete({
      data: mapStore(state),
    });
  },
});

export const qalTrackEngagementAction = createQALTrackEvent<AnalyticsStandardEvent>({
  name: 'legacyEngagementTracking',
  handler: (payload, state) => {
    analytics.trackCustomEvent({
      event: payload.event || AnalyticsStandardEvents.Standard,
      eventAction: payload.eventAction || analyticsConstant.NOT_SET,
      eventLabel: payload.eventLabel || analyticsConstant.NOT_SET,
      data: {
        ...payload.data,
        ...mapStore(state),
      },
    });
  },
});
